import { Injectable } from '@angular/core';

import { StoreOrigin } from '../models/store-origin.model';

interface StoredSignupOrigin {
  origin: StoreOrigin;
  capturedAt: number;
}

/**
 * Origem de marketing do cadastro (UTM, gclid, fbclid), capturada na entrada do app, antes de
 * qualquer navegação interna trocar a URL. Primeiro toque: a origem com parâmetros fica guardada
 * por 30 dias (sobrevive a reload e a sair e voltar ao cadastro) e é apagada depois do cadastro.
 */
@Injectable()
export class SignupOriginService {
  private static readonly STORAGE_KEY: string = 'signupOrigin';
  private static readonly MAX_AGE_MS: number = 30 * 24 * 60 * 60 * 1000;
  private static readonly ORIGIN_PARAMS: Array<string> = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'fbclid',
  ];

  // Entrada desta visita, mesmo sem parâmetros: dá referrer e landing_page ao cadastro sem origem
  private entry: StoreOrigin = null;

  capture(url: string = document.URL, referrer: string = document.referrer): void {
    const params: { [name: string]: string } = this.readParams(url);
    this.entry = {
      utmSource: params['utm_source'] || null,
      utmMedium: params['utm_medium'] || null,
      utmCampaign: params['utm_campaign'] || null,
      utmContent: params['utm_content'] || null,
      utmTerm: params['utm_term'] || null,
      gclid: params['gclid'] || null,
      fbclid: params['fbclid'] || null,
      referrer: referrer || null,
      landingPage: url,
    };
    const hasOrigin: boolean = SignupOriginService.ORIGIN_PARAMS.some((name) => !!params[name]);
    if (hasOrigin && !this.loadStored()) {
      this.store(this.entry);
    }
  }

  /** Origem a enviar no cadastro: a guardada (primeiro toque) ou a entrada desta visita */
  get(): StoreOrigin {
    return this.loadStored() || this.entry;
  }

  clear(): void {
    this.entry = null;
    try {
      localStorage.removeItem(SignupOriginService.STORAGE_KEY);
    } catch {
      // localStorage indisponível: não há o que apagar
    }
  }

  // Junta a query antes do "#" (onde anúncios inserem gclid/fbclid) e a query do hash
  // (formato dos links do site); se o parâmetro estiver nas duas, vale o do hash. Vazio não conta.
  private readParams(url: string): { [name: string]: string } {
    const params: { [name: string]: string } = {};
    const hashIndex: number = url.indexOf('#');
    const beforeHash: string = hashIndex === -1 ? url : url.substring(0, hashIndex);
    const hash: string = hashIndex === -1 ? '' : url.substring(hashIndex);
    [beforeHash, hash].forEach((part: string) => {
      const queryIndex: number = part.indexOf('?');
      if (queryIndex === -1) {
        return;
      }
      part
        .substring(queryIndex + 1)
        .split('&')
        .forEach((pair: string) => {
          const separatorIndex: number = pair.indexOf('=');
          const value: string =
            separatorIndex > 0 ? this.decode(pair.substring(separatorIndex + 1)) : '';
          if (value) {
            params[this.decode(pair.substring(0, separatorIndex))] = value;
          }
        });
    });
    return params;
  }

  private decode(value: string): string {
    try {
      return decodeURIComponent(value.replace(/\+/g, ' '));
    } catch {
      // Valor com % solto: guarda como veio
      return value;
    }
  }

  private loadStored(): StoreOrigin {
    try {
      const stored: StoredSignupOrigin = JSON.parse(
        localStorage.getItem(SignupOriginService.STORAGE_KEY)
      );
      if (
        stored &&
        stored.origin &&
        Date.now() - stored.capturedAt < SignupOriginService.MAX_AGE_MS
      ) {
        return stored.origin;
      }
    } catch {
      // Valor inválido ou localStorage indisponível: sem origem guardada
    }
    return null;
  }

  private store(origin: StoreOrigin): void {
    try {
      const stored: StoredSignupOrigin = { origin: origin, capturedAt: Date.now() };
      localStorage.setItem(SignupOriginService.STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // localStorage indisponível (navegação privada): vale só a entrada desta visita
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/signup-origin.service.ts